import { ZZFX_SAMPLE_RATE as SR } from '../lib/zzfx';
import { bp, env, hp, lp, noise, sweep } from './punch';

/**
 * Your KO's confirm (the killer's own sting, on every KO): a low thock — a sine diving 320 → 85 Hz, a woody knock of
 * band noise, a click — and a bright bell ringing over it (E6 with its fifth and octave, each a detuned pair so it
 * shimmers), driven through tanh. `tier` is the chain's step (1 KO … 5 ULTRA KO) and each one adds a layer: the bell's
 * major third and a longer ring (2), a sub drop under the thock (3), a second strike an octave up (4), a third strike
 * between them (5). Seeded noise: it renders the same every time.
 */
export function koSamples(tier: number): Float32Array {
  const t = Math.max(1, Math.min(5, Math.round(tier)));
  const out = new Float32Array(Math.round((0.45 + 0.15 * t) * SR));
  const add = (x: Float32Array, gain: number, at = 0): void => {
    const o = Math.round(at * SR);
    for (let i = 0; i < x.length && o + i < out.length; i++)
      out[o + i] = (out[o + i] as number) + gain * (x[i] as number);
  };
  // The thock.
  add(env(sweep(0.3, 320, 85, 0.01), 0.0005, 0.05), 0.9);
  add(env(bp(noise(0.15, 21), 220, 1.6), 0.0005, 0.028), 1.2);
  add(env(hp(lp(noise(0.03, 22), 9000), 2000), 0.0001, 0.0025), 1);
  // The bell, its partials (ratio, level, decay): the third from DOUBLE on.
  const tau = 0.13 + 0.035 * (t - 1);
  const partials = [[1, 1, 1], [1.5, 0.55, 0.8], [2, 0.3, 0.55], ...(t >= 2 ? [[1.25, 0.4, 0.9]] : [])] as const;
  const bell = (f: number, at: number, gain: number): void => {
    for (const [r, g, k] of partials) add(env(tone(0.45 + 0.15 * t, f * r), 0.0015, tau * k), gain * g, at);
  };
  bell(1318.5, 0.01, 0.45);
  if (t >= 3) add(env(sweep(0.7, 140, 48, 0.07), 0.003, 0.18), 0.75);
  if (t >= 5) bell(1975.5, 0.085, 0.28);
  if (t >= 4) bell(2637, t >= 5 ? 0.17 : 0.09, 0.28);
  // Driven, levelled to its peak, the silent tail cut with a 10 ms fade.
  let peak = 0;
  for (let i = 0; i < out.length; i++) {
    out[i] = Math.tanh(DRIVE * (out[i] as number));
    peak = Math.max(peak, Math.abs(out[i] as number));
  }
  const gain = (0.72 + 0.03 * (t - 1)) / peak;
  let end = out.length;
  while (end > 1 && Math.abs(out[end - 1] as number) < 0.002 * peak) end--;
  const x = out.slice(0, end).map((v) => v * gain);
  const fade = Math.min(Math.round(0.01 * SR), x.length);
  for (let i = 0; i < fade; i++) x[x.length - 1 - i] = (x[x.length - 1 - i] as number) * (i / fade);
  return x;
}

const DRIVE = 1.6;

/** A sine at `f` Hz as a pair 1.2 Hz apart: a slow beat, the shimmer of a struck bell. */
function tone(secs: number, f: number): Float32Array {
  const b = new Float32Array(Math.round(secs * SR));
  for (let i = 0; i < b.length; i++)
    b[i] = 0.5 * (Math.sin((2 * Math.PI * (f - 0.6) * i) / SR) + Math.sin((2 * Math.PI * (f + 0.6) * i) / SR));
  return b;
}
